import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Modal from "@amzn/awsui-components-core/modal";
import Multiselect from "@amzn/awsui-components-core/multiselect";
import Select from "@amzn/awsui-components-core/select";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import Textarea from "@amzn/awsui-components-core/textarea";
import ActionButtons from 'src/components/shared/ActionButtons';
import { SOPBatchEntry, SOPChange, SOPResource, fetchSOPBatches, createSOPBatch, updateSOPBatch, deleteSOPBatch } from 'src/services/sop-api';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { useUser } from 'src/context/UserContext';
import { greenButtonStyles, removeIconStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import './RecentSOPs.scss';
import mockData from 'src/mock/mockData.json';

// Toggle this to false to use the real API
const USE_MOCK_DATA = false;

const REGIONS = [
    { label: 'Global', value: 'Global' },
    { label: 'AMER', value: 'AMER' },
    { label: 'EMEA', value: 'EMEA' },
    { label: 'APAC', value: 'APAC' }
];

const CHANGE_TYPES = [
    { label: 'Guidance', value: 'Guidance - Includes add/remove/change to instructions, steps, or critical notes within the process' },
    { label: 'New Process', value: 'New Process' },
    { label: 'New User Guide', value: 'New User Guide' },
    { label: 'Resources', value: 'Resources - Includes add/remove/change to links, contact points, appendix items, or other reference materials' },
    { label: 'Verbiage', value: 'Verbiage - Includes add/remove/change to how steps are phrased or worded' },
];

const EXPIRY_DAYS = 7;

function isExpired(expiresAt: string): boolean {
    return new Date(expiresAt) < new Date();
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function emptyChange(): SOPChange {
    return {
        whatIsChanging: '',
        regionsImpacted: ['Global'],
        resources: [{ name: '', url: '' }],
        changeType: '',
        reasonForChange: '',
    };
}

const RecentSOPs = () => {
    const { canWrite } = useUser();
    const canEdit = canWrite('sopUpdates');

    const [batches, setBatches] = useState<SOPBatchEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingBatch, setEditingBatch] = useState<SOPBatchEntry | null>(null);
    const [editingChangeIdx, setEditingChangeIdx] = useState<number | null>(null);
    const [changes, setChanges] = useState<SOPChange[]>([emptyChange()]);
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{ batchId: string; changeIdx: number } | null>(null);
    const [collapsedDates, setCollapsedDates] = useState<Record<string, boolean>>({});
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();

    useEffect(() => {
        loadBatches();
    }, []);

    const loadBatches = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setBatches((mockData.sops?.items || []) as SOPBatchEntry[]);
            } else {
                const data = await fetchSOPBatches();
                setBatches(data.items || []);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load SOP updates');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditingBatch(null);
        setEditingChangeIdx(null);
        setChanges([emptyChange()]);
        setError(null);
        setShowForm(true);
    };

    const openEdit = (batch: SOPBatchEntry, changeIdx: number) => {
        setEditingBatch(batch);
        setEditingChangeIdx(changeIdx);
        const c = batch.changes[changeIdx];
        setChanges([{
            ...c,
            resources: c.resources.length > 0 ? c.resources : [{ name: '', url: '' }],
        }]);
        setError(null);
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingBatch(null);
        setEditingChangeIdx(null);
        setChanges([emptyChange()]);
        setError(null);
    };

    // --- Change row helpers ---
    const updateChange = (idx: number, field: keyof SOPChange, value: any) => {
        setChanges(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    };

    const addChange = () => setChanges(prev => [...prev, emptyChange()]);

    const removeChange = (idx: number) => {
        setChanges(prev => prev.filter((_, i) => i !== idx));
    };

    // --- Resource helpers ---
    const updateResource = (changeIdx: number, resIdx: number, field: keyof SOPResource, value: string) => {
        setChanges(prev => prev.map((c, i) => {
            if (i !== changeIdx) return c;
            const resources = c.resources.map((r, j) => j === resIdx ? { ...r, [field]: value } : r);
            return { ...c, resources };
        }));
    };

    const addResource = (changeIdx: number) => {
        setChanges(prev => prev.map((c, i) =>
            i === changeIdx ? { ...c, resources: [...c.resources, { name: '', url: '' }] } : c
        ));
    };

    const removeResource = (changeIdx: number, resIdx: number) => {
        setChanges(prev => prev.map((c, i) => {
            if (i !== changeIdx) return c;
            return { ...c, resources: c.resources.filter((_, j) => j !== resIdx) };
        }));
    };

    // Form is valid when every change row has its required fields filled
    // (What is Changing, Change Type, Reason for Change) and no resource has a
    // name without a URL. Mirrors the checks in handleSave.
    const isFormValid = (): boolean => {
        return changes.every(c =>
            c.whatIsChanging.trim() !== '' &&
            c.changeType !== '' &&
            c.reasonForChange.trim() !== '' &&
            c.resources.every(r => !(r.name.trim() && !r.url.trim()))
        );
    };

    const handleSave = async () => {
        // Validate
        for (let i = 0; i < changes.length; i++) {
            const c = changes[i];
            if (!c.whatIsChanging.trim()) { setError(`Row ${i + 1}: "What is Changing" is required`); return; }
            if (!c.changeType) { setError(`Row ${i + 1}: "Change Type" is required`); return; }
            if (!c.reasonForChange.trim()) { setError(`Row ${i + 1}: "Reason for Change" is required`); return; }
            for (const r of c.resources) {
                if (r.name.trim() && !r.url.trim()) { setError(`Row ${i + 1}: Resource "${r.name}" is missing a URL`); return; }
            }
        }

        // Strip empty resources
        const cleanedChanges = changes.map(c => ({
            ...c,
            resources: c.resources.filter(r => r.name.trim() || r.url.trim()),
        }));

        setSubmitting(true);
        try {
            if (editingBatch && editingChangeIdx !== null) {
                // Update only the single edited change row within the batch
                const updatedChanges = [...editingBatch.changes];
                updatedChanges[editingChangeIdx] = cleanedChanges[0];
                const updated = await updateSOPBatch(editingBatch.sopBatchId, { changes: updatedChanges });
                setBatches(prev => prev.map(b => b.sopBatchId === editingBatch.sopBatchId ? updated : b));
                setSuccessMsg('SOP update saved');
            } else if (editingBatch) {
                const updated = await updateSOPBatch(editingBatch.sopBatchId, { changes: cleanedChanges });
                setBatches(prev => prev.map(b => b.sopBatchId === editingBatch.sopBatchId ? updated : b));
                setSuccessMsg('SOP update saved');
            } else {
                const created = await createSOPBatch({ changes: cleanedChanges });
                setBatches(prev => [created, ...prev]);
                setSuccessMsg('SOP update published');
            }
            closeForm();
        } catch (err: any) {
            setError(err.message || 'Failed to save SOP update');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const batch = batches.find(b => b.sopBatchId === deleteTarget.batchId);
        if (!batch) {
            setDeleteTarget(null);
            return;
        }
        try {
            const remainingChanges = batch.changes.filter((_, i) => i !== deleteTarget.changeIdx);
            if (remainingChanges.length === 0) {
                // No changes left — delete the entire batch
                await deleteSOPBatch(deleteTarget.batchId);
                setBatches(prev => prev.filter(b => b.sopBatchId !== deleteTarget.batchId));
            } else {
                // Remove just this change and keep the batch
                const updated = await updateSOPBatch(deleteTarget.batchId, { changes: remainingChanges });
                setBatches(prev => prev.map(b => b.sopBatchId === deleteTarget.batchId ? updated : b));
            }
            setSuccessMsg('SOP update deleted');
        } catch (err: any) {
            setError(err.message || 'Failed to delete SOP update');
        }
        setDeleteTarget(null);
    };

    // Only show non-expired batches
    const activeBatches = batches.filter(b => !isExpired(b.expiresAt));

    // Group batches by publish date for display
    const groupedByDate = activeBatches.reduce<Record<string, SOPBatchEntry[]>>((acc, batch) => {
        const dateKey = formatDate(batch.publishedAt);
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(batch);
        return acc;
    }, {});

    return (
        <div className="section-card recent-sops">
            <div className="section-card__header">
                <h3 className="section-card__title">Recently Updated SOPs</h3>
                {canEdit && (  
                    <Button
                        style={greenButtonStyles}
                        onClick={openCreate}
                    >
                        New SOP Update
                    </Button>
                )}
            </div>

            <div className="section-card__body">
            <div ref={alertRef} tabIndex={-1}>
                {error && !showForm && (
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                )}
                {successMsg && (
                    <Alert
                        dismissible
                        type="success"
                        onDismiss={() => setSuccessMsg(null)}
                    >
                        {successMsg}
                    </Alert>
                )}
            </div>

            {loading && <Spinner />}

            {!loading && activeBatches.length === 0 && (
                <p className="section-card__placeholder">
                    No SOP updates in the last {EXPIRY_DAYS} days.
                </p>
            )}

            {/* Form Modal */}
            {showForm && (
                <Modal 
                    visible 
                    size="xx-large"
                    onDismiss={ closeForm }
                    header={ editingBatch ? 'Edit SOP Update' : 'New SOP Update' }
                    footer={
                        <div className="sop-form__footer">
                            {requiredFieldHint()}
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button 
                                    variant="link" 
                                    onClick={closeForm}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="primary" 
                                    disabled={submitting || !isFormValid()} 
                                    onClick={handleSave}
                                >
                                    {submitting ? 'Saving...' : (editingBatch ? 'Save Changes' : 'Publish')}
                                </Button>
                            </SpaceBetween>
                        </div>
                    }
                >
                    {error && (
                        <div className="alert">
                            <Alert
                                dismissible
                                type="error"
                                onDismiss={() => setError(null)}
                            >
                                <strong>Error: </strong>{error}
                            </Alert>
                        </div>
                    )}

                    <div className="sop-form__table-wrapper">
                        <table className="sop-table sop-table--form">
                            <thead>
                                <tr>
                                    <th>What is Changing {requiredMark()}</th>
                                    <th>Region(s) Impacted</th>
                                    <th>Resource(s)</th>
                                    <th>Change Type {requiredMark()}</th>
                                    <th>Reason for Change {requiredMark()}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {changes.map((change, idx) => (
                                    <tr key={idx} className="sop-table__row">
                                        <td>
                                            <Textarea
                                                placeholder="Describe what is changing"
                                                rows={5}
                                                value={change.whatIsChanging}
                                                onChange={({ detail }) => updateChange(idx, 'whatIsChanging', detail.value)}
                                            />
                                        </td>
                                        <td>
                                            <Multiselect
                                                placeholder="Select Region"
                                                options={REGIONS}
                                                selectedOptions={REGIONS.filter(t => change.regionsImpacted.includes(t.value))}
                                                onChange={({ detail }) => { 
                                                    const val = detail.selectedOptions.map(reg => reg.value as string);
                                                    updateChange(idx, 'regionsImpacted', val);
                                                }}
                                                enableSelectAll
                                                i18nStrings={{
                                                    selectAllText: "Select all"
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <div className="sop-form__resources">
                                                {change.resources.map((res, rIdx) => (
                                                    <div key={rIdx} className="sop-form__resource-row">
                                                        <Input
                                                            placeholder="Display name"
                                                            value={res.name}
                                                            onChange={({ detail }) => updateResource(idx, rIdx, 'name', detail.value)}
                                                        />
                                                        <Input
                                                            placeholder="https://..."
                                                            value={res.url}
                                                            onChange={({ detail }) => updateResource(idx, rIdx, 'url', detail.value)}
                                                        />
                                                        {change.resources.length > 1 && (
                                                            <span style={{ display: 'flex', justifyContent: 'center' }}>
                                                            <Button
                                                                variant="link"
                                                                style={removeIconStyles}
                                                                onClick={() => removeResource(idx, rIdx)}
                                                            >
                                                                <Icon name="close" size="x-small" />
                                                            </Button>
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                                <Button
                                                    variant="inline-link"
                                                    onClick={() => addResource(idx)}
                                                >
                                                    + Add resource
                                                </Button>
                                            </div>
                                        </td>
                                        <td>
                                            <Select
                                                placeholder="Select Change Type"
                                                options={CHANGE_TYPES}
                                                selectedOption={CHANGE_TYPES.find(t => t.value === change.changeType) || null}
                                                onChange={({ detail }) => { 
                                                    updateChange(idx, 'changeType', detail.selectedOption?.value || ''); 
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <Textarea
                                                placeholder="Reason for this change"
                                                rows={5}
                                                value={change.reasonForChange}
                                                onChange={({ detail }) => updateChange(idx, 'reasonForChange', detail.value)}
                                            />
                                        </td>
                                        <td>
                                            {changes.length > 1 && (
                                                <Button
                                                    variant="link"
                                                    onClick={() => removeChange(idx)}
                                                    style={removeIconStyles}
                                                >
                                                    <Icon name="remove" size="small" />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="sop-form__row-actions">
                        {editingChangeIdx === null && (
                            <Button 
                                variant="inline-link"
                                onClick={addChange} 
                            >
                                + Add Row
                            </Button>
                        )}
                    </div>
                </Modal>
            )}

            {/* Read view — grouped by date */}
            {!loading && Object.entries(groupedByDate).map(([dateLabel, dateBatches], groupIdx) => {
                const isCollapsed = collapsedDates[dateLabel] !== undefined ? collapsedDates[dateLabel] : groupIdx !== 0;
                return (
                <div key={dateLabel} className="sop-batch">
                    <div className="sop-batch__header" onClick={() => setCollapsedDates(prev => ({ ...prev, [dateLabel]: !isCollapsed }))}>
                        <span className="sop-batch__date">
                            Published {dateLabel}
                        </span>
                        <span className="expandable-container">
                            <Icon
                                name={isCollapsed ? "caret-right-filled" : "caret-down-filled"}
                                size="x-small"
                                variant="subtle"
                            />
                        </span>
                    </div>

                    { !isCollapsed && (
                        <div className="sop-cards">
                            {dateBatches.reduce<React.ReactNode[]>((cards, batch) => {
                                batch.changes.forEach((change: SOPChange, idx: number) => {
                                    cards.push(
                                        <Container key={`${batch.sopBatchId}-${idx}`}>
                                            <div className="sop-card__header">
                                                <div className="sop-card__badges">
                                                    <span className="sop-card__change-type">{change.changeType?.split('-')[0]}</span>
                                                    <span className="sop-card__region">{change.regionsImpacted.join(', ')}</span>
                                                </div>
                                                { canEdit && (
                                                    <div className="sop-card__actions">
                                                         <ActionButtons
                                                            onEdit={() => openEdit(batch, idx)}
                                                            onDelete={() => setDeleteTarget({ batchId: batch.sopBatchId, changeIdx: idx })}
                                                            disabled={showForm}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="sop-card__body">
                                                <div className="sop-card__field">
                                                    <span className="sop-card__label">What is Changing</span>
                                                    <span className="sop-card__value">{change.whatIsChanging}</span>
                                                </div>

                                                {change.resources.length > 0 && change.resources.some(r => r.name || r.url) && (
                                                    <div className="sop-card__field">
                                                        <span className="sop-card__label">Resource(s)</span>
                                                        <div className="sop-card__value">
                                                            {change.resources.map((r, rIdx) => (
                                                                <div key={rIdx}>
                                                                    {r.url ? (
                                                                        <a href={r.url} target="_blank" rel="noopener noreferrer">
                                                                            {r.name || r.url}
                                                                        </a>
                                                                    ) : (
                                                                        <span>{r.name}</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="sop-card__field">
                                                    <span className="sop-card__label">Reason for Change</span>
                                                    <span className="sop-card__value">{change.reasonForChange}</span>
                                                </div>
                                            </div>
                                        </Container>
                                    );
                                });
                                return cards;
                            }, [])}
                        </div>
                    )}
                </div>
                );
            })}
            </div>

            {/* Delete confirmation */}
            {deleteTarget && (
                <Modal 
                    visible 
                    header="Delete SOP Update"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                        </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this SOP update? This cannot be undone.
                </Modal>
            )}
        </div>
    );
};

export default RecentSOPs;
